/**
 * MarkerOverlay - Auto-popup overlay when timeline passes a marker
 * Shows event details, player info, and analysis
 */

import React, { useState, useEffect } from 'react';
import {
  X,
  ChevronDown,
  ChevronUp,
  Zap,
  Target,
  TrendingUp,
  Shield,
  Activity,
  Brain,
  Eye,
  AlertTriangle,
  Star,
  CircleDot,
} from 'lucide-react';

const categoryIcons = {
  clutch: Star,
  shooting: Target,
  drive: TrendingUp,
  playmaking: Zap,
  defense: Shield,
  rebounding: CircleDot,
  post: Activity,
  biomechanics: Activity,
  fatigue: AlertTriangle,
  psychology: Brain,
  officiating: Eye,
  dagger: Zap,
};

const categoryColors = {
  clutch: { bg: 'bg-yellow-500/30', border: 'border-yellow-500/60', text: 'text-yellow-400' },
  shooting: { bg: 'bg-green-500/30', border: 'border-green-500/60', text: 'text-green-400' },
  drive: { bg: 'bg-blue-500/30', border: 'border-blue-500/60', text: 'text-blue-400' },
  playmaking: { bg: 'bg-purple-500/30', border: 'border-purple-500/60', text: 'text-purple-400' },
  defense: { bg: 'bg-red-500/30', border: 'border-red-500/60', text: 'text-red-400' },
  rebounding: { bg: 'bg-orange-500/30', border: 'border-orange-500/60', text: 'text-orange-400' },
  post: { bg: 'bg-pink-500/30', border: 'border-pink-500/60', text: 'text-pink-400' },
  biomechanics: { bg: 'bg-cyan-500/30', border: 'border-cyan-500/60', text: 'text-cyan-400' },
  fatigue: { bg: 'bg-amber-500/30', border: 'border-amber-500/60', text: 'text-amber-400' },
  psychology: { bg: 'bg-indigo-500/30', border: 'border-indigo-500/60', text: 'text-indigo-400' },
  officiating: { bg: 'bg-stone-500/30', border: 'border-stone-500/60', text: 'text-stone-400' },
  dagger: { bg: 'bg-red-600/30', border: 'border-red-600/60', text: 'text-red-500' },
};

const MarkerOverlay = ({
  marker,
  onDismiss,
  onResume,
  position = 'top-left', // 'top-left', 'top-right', 'bottom-left', 'bottom-right'
  autoCollapseAfter = 0, // 0 = don't auto-collapse
}) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const [hasInteracted, setHasInteracted] = useState(false);

  // Reset state when marker changes
  useEffect(() => {
    setIsExpanded(true);
    setHasInteracted(false);
  }, [marker?.id]);

  // Auto-collapse timer
  useEffect(() => {
    if (!marker || autoCollapseAfter === 0 || hasInteracted) return;

    const timer = setTimeout(() => {
      setIsExpanded(false);
      onResume?.();
    }, autoCollapseAfter);

    return () => clearTimeout(timer);
  }, [marker, autoCollapseAfter, hasInteracted, onResume]);

  if (!marker) return null;

  const Icon = categoryIcons[marker.category] || Zap;
  const colors = categoryColors[marker.category] || categoryColors.clutch;

  // Position classes
  const positionClasses = {
    'top-left': 'top-4 left-4',
    'top-right': 'top-4 right-4',
    'bottom-left': 'bottom-24 left-4',
    'bottom-right': 'bottom-24 right-4',
  };

  const handleInteraction = () => {
    setHasInteracted(true);
  };

  return (
    <div
      className={`absolute ${positionClasses[position]} z-30 max-w-sm animate-fadeIn`}
      onClick={(e) => {
        e.stopPropagation();
        handleInteraction();
      }}
    >
      {!isExpanded ? (
        // Collapsed state - compact pill
        <button
          onClick={() => setIsExpanded(true)}
          className={`flex items-center gap-2 px-3 py-2 bg-black/90 backdrop-blur-sm rounded-lg border ${colors.border} hover:bg-black/95 transition-colors`}
        >
          <div className={`w-2 h-2 rounded-full animate-pulse ${colors.text.replace('text-', 'bg-')}`} />
          <span className="text-white text-sm font-medium truncate max-w-[200px]">
            {marker.event}
          </span>
          <span className={`text-xs px-1.5 py-0.5 rounded font-mono ${colors.bg} ${colors.text}`}>
            {marker.timeVideo}
          </span>
          <ChevronDown className="w-4 h-4 text-white/60" />
        </button>
      ) : (
        // Expanded state - full card with translucent category color fill
        <div
          className={`backdrop-blur-xl rounded-xl border-2 shadow-2xl overflow-hidden ${colors.border} ${colors.bg}`}
        >
          {/* Header - slightly lighter than body */}
          <div className="px-4 py-3 flex items-center justify-between bg-black/20">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${colors.text.replace('text-', 'bg-')}`}>
                <Icon className="w-5 h-5 text-white" />
              </div>
              <div>
                <span className="font-bold text-white text-base block">
                  {marker.event}
                </span>
                <div className="flex items-center gap-2 mt-0.5">
                  {marker.player && (
                    <span className="px-1.5 py-0.5 bg-white/20 text-white text-xs font-medium rounded">
                      {marker.player}
                    </span>
                  )}
                  <span className={`text-xs px-1.5 py-0.5 rounded font-mono ${colors.bg} ${colors.text}`}>
                    {marker.timeVideo}
                  </span>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setIsExpanded(false)}
                className="p-1 hover:bg-white/20 rounded transition-colors"
              >
                <ChevronUp className="w-4 h-4 text-white/60" />
              </button>
              <button
                onClick={onDismiss}
                className="p-1 hover:bg-white/20 rounded transition-colors"
              >
                <X className="w-4 h-4 text-white/60" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="p-4">
            {/* Team badge */}
            {marker.team && (
              <div className="flex items-center gap-2 mb-3">
                <span className={`px-2 py-1 text-xs font-bold rounded ${
                  marker.team === 'Real Madrid'
                    ? 'bg-white text-black'
                    : 'bg-blue-800 text-white'
                }`}>
                  {marker.team}
                </span>
                <span className="text-white/60 text-xs uppercase tracking-wider">
                  {marker.category}
                </span>
              </div>
            )}

            {/* Analysis text */}
            <p className="text-white/90 text-sm leading-relaxed">
              {marker.analysis}
            </p>

            {/* Confidence indicator */}
            {marker.confidence && (
              <div className="mt-4 flex items-center gap-3">
                <div className="flex-1 h-1.5 bg-white/10 rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full ${colors.text.replace('text-', 'bg-')}`}
                    style={{ width: `${marker.confidence * 100}%` }}
                  />
                </div>
                <span className={`text-xs font-mono ${colors.text}`}>
                  {(marker.confidence * 100).toFixed(0)}% confidence
                </span>
              </div>
            )}
          </div>

          {/* Footer actions */}
          <div className="px-4 pb-4 flex items-center justify-between">
            <button
              onClick={() => {
                setIsExpanded(false);
                onResume?.();
              }}
              className="px-3 py-1.5 bg-white/10 hover:bg-white/20 text-white text-xs rounded-lg transition-colors"
            >
              Continue Playback
            </button>
            <div className="text-white/40 text-xs">
              Press Space to resume
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MarkerOverlay;

// CSS for fadeIn animation (add to your global CSS or use inline)
const styles = `
  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(-8px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  .animate-fadeIn {
    animation: fadeIn 0.2s ease-out;
  }
`;
